import { getPerPlatformRateRows } from "../../utils/campaignRates";

/** Badge tone and label for each campaign status. */
export const STATUS_TONES = {
  active: "success",
  pending: "warning",
  paused: "neutral",
  completed: "neutral",
  cancelled: "danger",
};

export const STATUS_KEYS = {
  active: "common.statusActive",
  pending: "common.statusPending",
  paused: "common.statusPaused",
  completed: "common.statusCompleted",
  cancelled: "common.statusCancelled",
};

const formatRate = (n) => String(Number(Number(n).toFixed(2)));

/** A number as Figma's All view and Featured write it: "3 200", "19,99" in Georgian, "3,200", "19.99" in English. */
export const formatNumber = (n, language) => {
  const text = Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 });
  // Spelled out: not every browser ships the Georgian locale.
  return language === "ka" ? text.replace(/,/g, " ").replace(".", ",") : text;
};

/** A star rating, always one decimal: "5.0", "4,8" in Georgian. */
export const formatRating = (n, language) => {
  const text = Number(n || 0).toFixed(1);
  return language === "ka" ? text.replace(".", ",") : text;
};

/** "3 200 ₾" in Georgian, "₾3,200" in English. */
/** Who to name on a public card. Some usernames are email addresses, so never show past the @. */
export const ownerName = (user) => (user?.businessName || user?.username || "").split("@")[0];

export const formatMoney = (n, language) =>
  language === "ka" ? `${formatNumber(n, language)} ₾` : `₾${formatNumber(n, language)}`;

/** "₾1,150 paid out of ₾2,000", the paid part in mint. */
export const PaidOutOf = ({ paid, total, language, t }) => {
  const [before, after] = t("home.budgetPaidOut", { total: formatMoney(total, language) }).split("{paid}");
  return (
    <span className="whitespace-normal text-footnote-semibold">
      {before}
      <span className="whitespace-nowrap text-accent-text">{formatMoney(paid, language)}</span>
      {after}
    </span>
  );
};

/**
 * "0.1 ₾" for one rate, "1–3 ₾" across platform rates, "—" when none is set.
 * With `language`, in the All view's format: "1,5–3 ₾" or "₾1.5–3".
 */
export function campaignRateText(campaign, language) {
  const rates = getPerPlatformRateRows(campaign).map((row) => row.rate);
  if (rates.length === 0) return "—";
  const low = Math.min(...rates);
  const high = Math.max(...rates);
  if (language) {
    return low === high
      ? formatMoney(low, language)
      : formatMoney(0, language).replace("0", `${formatNumber(low, language)}–${formatNumber(high, language)}`);
  }
  return low === high ? `${formatRate(low)} ₾` : `${formatRate(low)}–${formatRate(high)} ₾`;
}

/** Whole days until the end date; null when it has none or it has passed. */
export function daysLeft(campaign, now = Date.now()) {
  if (campaign?.noEndDate || !campaign?.endDate) return null;
  const end = new Date(campaign.endDate).getTime();
  if (!Number.isFinite(end) || end < now) return null;
  return Math.ceil((end - now) / 86400000);
}
